import {
  BarChart,
  CampaignRounded,
  Devices,
  FiberManualRecord,
  FormatListBulletedRounded,
  Groups,
  Home,
  ManageAccounts,
  NotificationsRounded,
  Payment,
  Phone,
  History,
  Settings,
  SupportRounded,
  AllInbox,
  Insights,
  Policy,
} from '@mui/icons-material'
import { cloneDeep, isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import useHasVoipOrgFeature from '~/features/organizations/hooks/useHasVoipOrgFeature'
import useIsPartner from '~/features/partners/hooks/useIsPartner'
import { ESettingsStepFriendlyNames } from '~/features/settings/models'
import { EBaseAccess, ECapability } from '~/features/user/models'
import useCapabilities from '~/hooks/useCapabilities'
import EHeyLibbyAdminNavItemNames from '~/layouts/account/constants/navItems'
import {
  ENavItemNames,
  ENavSectionNames,
  TNavItemChild,
  TNavItem,
  EDocsPath,
} from '~/layouts/account/models'
import {
  EAdminPortalClientPath,
  EClientPath,
  EMarketingClientPath,
} from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import rem from '~/utils/ui'

const heyLibbyAdminNavItems: TNavItem[] = [
  {
    name: EHeyLibbyAdminNavItemNames.home,
    icon: <Home sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.admin,
  },
  {
    name: EHeyLibbyAdminNavItemNames.orgsAndUsers,
    icon: <Groups sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.orgsAndUsers,
  },
  {
    name: EHeyLibbyAdminNavItemNames.reports,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.reports,
  },

  {
    name: EHeyLibbyAdminNavItemNames.referrals,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.referrals,
  },
  {
    name: EHeyLibbyAdminNavItemNames.partnerManagement,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.partnerManagement,
  },
  {
    name: EHeyLibbyAdminNavItemNames.phoneNumbers,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    path: EAdminPortalClientPath.phoneNumbers,
  },
  {
    name: EHeyLibbyAdminNavItemNames.siteMetadata,
    icon: <FiberManualRecord sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: EHeyLibbyAdminNavItemNames.serviceTemplates,
        path: EAdminPortalClientPath.serviceTemplates,
      },
      {
        name: EHeyLibbyAdminNavItemNames.promptMetadata,
        path: EAdminPortalClientPath.promptMetadata,
      },
      {
        name: EHeyLibbyAdminNavItemNames.campaignTemplates,
        path: EAdminPortalClientPath.campaignTemplates,
      },
      {
        name: EHeyLibbyAdminNavItemNames.voiceTemplates,
        path: EAdminPortalClientPath.voiceTemplates,
      },
      {
        name: EHeyLibbyAdminNavItemNames.planMetadata,
        path: EAdminPortalClientPath.planMetadata,
      },
      {
        name: EHeyLibbyAdminNavItemNames.adminPortalConfig,
        path: EAdminPortalClientPath.portalGeneralSettings,
        children: [
          {
            name: EHeyLibbyAdminNavItemNames.adminPortalGeneralSettings,
            path: EAdminPortalClientPath.portalGeneralSettings,
          },
          {
            name: EHeyLibbyAdminNavItemNames.adminPortalBranding,
            path: EAdminPortalClientPath.portalBranding,
          },
        ],
      },
    ],
  },
]

const navItems: TNavItem[] = [
  {
    name: ENavItemNames.overview,
    icon: <Home sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.overview,
  },
  {
    name: ENavSectionNames.assistantSetup,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.conversationSettings,
    icon: <Settings sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: ENavItemNames.settingsCoreBehavior,
        path: EClientPath.settingsCoreBehavior,
      },
      {
        name: ESettingsStepFriendlyNames.knowledge,
        path: EClientPath.settingsKnowledge,
      },
      import.meta.env[EEnvironmentVariables.VITE_IS_LIVE_TRANSFERS_ENABLED] ===
      'true'
        ? {
            name: ENavItemNames.liveTransfers,
            path: EClientPath.liveTransfersScenarios,
          }
        : undefined,
      {
        name: ESettingsStepFriendlyNames.escalations,
        path: EClientPath.settingsEscalations,
      },
      {
        name: ENavItemNames.scenarios,
        path: EClientPath.manageScenariosV2,
        children: [
          {
            name: ENavItemNames.actions,
            path: EClientPath.manageScenariosActions,
          },
        ],
      },
    ].filter(Boolean) as TNavItemChild[],
  },
  {
    name: ENavItemNames.channels,
    icon: <Devices sx={{ width: rem(17), height: rem(17) }} />,
    children: [
      {
        name: ESettingsStepFriendlyNames.chatbot,
        path: EClientPath.settingsChatbot,
      },
      {
        name: ESettingsStepFriendlyNames.email,
        path: EClientPath.settingsEmail,
      },
      {
        name: ESettingsStepFriendlyNames.text,
        path: EClientPath.settingsText,
      },
      {
        name: ESettingsStepFriendlyNames.phone,
        path: EClientPath.settingsPhone,
      },
    ],
  },
  {
    name: ENavSectionNames.campaigns,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.campaignManager,
    icon: <CampaignRounded sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.campaignList,
  },
  {
    name: ENavSectionNames.voip,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.voipPhoneNumbers,
    icon: <Phone sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.voipPhoneNumbers,
  },
  {
    name: ENavItemNames.voipDevices,
    icon: <Devices sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.voipDevices,
  },
  {
    name: ENavItemNames.voipCalls,
    icon: <History sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.voipCalls,
  },
  {
    name: ENavSectionNames.conversations,
    isSectionHeader: true,
  },
  {
    name: ENavItemNames.notifications,
    icon: <NotificationsRounded sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.settingsNotifications,
  },
  {
    name: ENavItemNames.postConvoSettings,
    icon: <Insights sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.settingsAnalysisSettingsV2,
  },
  {
    name: ENavItemNames.inbox,
    icon: <AllInbox sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.inbox,
  },
  {
    name: ENavItemNames.contacts,
    icon: (
      <FormatListBulletedRounded sx={{ width: rem(17), height: rem(17) }} />
    ),
    path: EClientPath.contacts,
  },
  {
    name: ENavItemNames.analytics,
    icon: <BarChart sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.reports,
  },
  {
    name: ENavItemNames.account,
    icon: <ManageAccounts sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.account,
  },
  {
    name: ENavItemNames.billing,
    icon: <Payment sx={{ width: rem(17), height: rem(17) }} />,
    path: EClientPath.billing,
  },

  // Hide for now, uncomment code once is ready
  // {
  //   name: ENavItemNames.referrals,
  //   icon: (
  //     <AttachMoney
  //       sx={{ width: rem(23), height: rem(23), marginLeft: rem(-3) }}
  //     />
  //   ),
  //   path: EClientPath.referralsInfo,
  //   isOnBottom: true,
  // },
].filter(Boolean) as TNavItem[]

const replifyHelpNavLinks: TNavItem = {
  name: ENavItemNames.help,
  icon: <SupportRounded sx={{ width: rem(17), height: rem(17) }} />,
  isOnBottom: true,
  children: [
    {
      name: ENavItemNames.helpCenter,
      path: EDocsPath.root,
      openInNewTab: true,
    },
    {
      name: ENavItemNames.integrations,
      path: EDocsPath.zapierCrmIntegration,
      openInNewTab: true,
    },
    {
      name: ENavItemNames.apiReference,
      path: EDocsPath.apiKeys,
      openInNewTab: true,
    },
  ],
}

const replifyTermsAndPoliciesNavLinks: TNavItem = {
  name: ENavItemNames.termsAndPolicies,
  icon: <Policy sx={{ width: rem(17), height: rem(17) }} />,
  isOnBottom: true,
  children: [
    {
      name: ENavItemNames.termsOfUse,
      path: EMarketingClientPath.marketingTermsOfUse,
      openInNewTab: true,
    },
    {
      name: ENavItemNames.privacyPolicy,
      path: EMarketingClientPath.marketingPrivacyPolicy,
      openInNewTab: true,
    },
  ],
}

const partnerHelpNavLinks: TNavItem = {
  name: ENavItemNames.help,
  icon: <SupportRounded sx={{ width: rem(17), height: rem(17) }} />,
  isOnBottom: true,
  children: [
    {
      name: ENavItemNames.support,
      path: EClientPath.support,
    },
    {
      name: ENavItemNames.termsOfUse,
      path: EClientPath.termsOfUse,
    },
    {
      name: ENavItemNames.privacyPolicy,
      path: EClientPath.privacyPolicy,
    },
    {
      name: ENavItemNames.referralsTerms,
      path: EClientPath.referralsTerms,
    },
  ],
}

const filterNavItemChildren = (
  children: TNavItemChild[],
  namesToRemove: (
    | ESettingsStepFriendlyNames
    | ENavItemNames
    | EHeyLibbyAdminNavItemNames
    | ENavSectionNames
  )[],
): TNavItemChild[] => {
  return children.filter((child) => {
    const name = 'name' in child ? child.name : null
    return name && !namesToRemove.includes(name)
  })
}

const filterNavItems = (
  data: TNavItem[],
  namesToRemove: (
    | ESettingsStepFriendlyNames
    | ENavItemNames
    | ENavSectionNames
  )[],
): TNavItem[] => {
  return data.reduce((filteredList: TNavItem[], item: TNavItem) => {
    if (
      !namesToRemove.includes(
        item.name as
          | ESettingsStepFriendlyNames
          | ENavItemNames
          | ENavSectionNames,
      )
    ) {
      const newItem = cloneDeep(item)
      if (newItem.children) {
        newItem.children = filterNavItemChildren(
          newItem.children,
          namesToRemove,
        )
      }
      filteredList.push(newItem)
    }
    return filteredList
  }, [])
}
export const useNavItems = () => {
  const isPartner = useIsPartner()
  const { selectedAgent } = useGetAgentCentralized()
  const location = useLocation()
  const { baseAccess, canRead } = useCapabilities()
  const {
    canAccessAiAssistantConfig,
    canAccessKnowledgeBase,
    canAccessVoipConfig,
    canAccessVoipVoicemails,
  } = useUserAccess()
  const isCustomAccess = baseAccess === EBaseAccess.custom
  const canReadCrm = canRead(ECapability.crm)
  const canReadReporting = canRead(ECapability.reporting)
  const canReadCampaigns = canRead(ECapability.campaigns)
  const canReadVoipConfig = canRead(ECapability.voipConfig)
  const canReadVoipVoicemails = canRead(ECapability.voipVoicemails)

  const shouldDisplayHeyLibbyAdminNavItems = location.pathname.includes(
    EAdminPortalClientPath.admin,
  )

  const hasVoipOrgFeature = useHasVoipOrgFeature()

  const navItemNamesHiddenForPartners: Array<
    ENavItemNames | ESettingsStepFriendlyNames | ENavSectionNames
  > = [
    ENavItemNames.account,
    ENavItemNames.billing,
    ENavItemNames.referrals,
    ENavItemNames.referralsTerms,
  ]

  const hiddenNavItemsForHeyLibby: Array<
    ENavItemNames | ESettingsStepFriendlyNames | ENavSectionNames
  > = [ENavItemNames.billing, ENavItemNames.account]

  const allVoipNavNames: Array<ENavItemNames | ENavSectionNames> = [
    ENavSectionNames.voip,
    ENavItemNames.voipPhoneNumbers,
    ENavItemNames.voipDevices,
    ENavItemNames.voipCalls,
  ]

  if (!hasVoipOrgFeature) {
    navItemNamesHiddenForPartners.push(...allVoipNavNames)
    hiddenNavItemsForHeyLibby.push(...allVoipNavNames)
  } else {
    if (!canReadVoipConfig) {
      navItemNamesHiddenForPartners.push(
        ENavItemNames.voipPhoneNumbers,
        ENavItemNames.voipDevices,
      )
      hiddenNavItemsForHeyLibby.push(
        ENavItemNames.voipPhoneNumbers,
        ENavItemNames.voipDevices,
      )
    }
    if (!canReadVoipVoicemails) {
      navItemNamesHiddenForPartners.push(ENavItemNames.voipCalls)
      hiddenNavItemsForHeyLibby.push(ENavItemNames.voipCalls)
    }
  }

  // Add escalations to hidden nav items if the agent has no escalations
  if (isEmpty(selectedAgent?.escalations)) {
    if (isPartner) {
      navItemNamesHiddenForPartners.push(ESettingsStepFriendlyNames.escalations)
    } else {
      hiddenNavItemsForHeyLibby.push(ESettingsStepFriendlyNames.escalations)
    }
  }

  if (shouldDisplayHeyLibbyAdminNavItems) {
    return heyLibbyAdminNavItems
  }

  if (isCustomAccess) {
    const customAccessNavItems: TNavItem[] = []
    const overviewItem = navItems.find(
      ({ name }) => name === ENavItemNames.overview,
    )
    const conversationsHeader = navItems.find(
      ({ name }) => name === ENavSectionNames.conversations,
    )
    const inboxItem = navItems.find(({ name }) => name === ENavItemNames.inbox)
    const contactsItem = navItems.find(
      ({ name }) => name === ENavItemNames.contacts,
    )
    const reportsItem = navItems.find(
      ({ name }) => name === ENavItemNames.analytics,
    )
    const campaignsHeader = navItems.find(
      ({ name }) => name === ENavSectionNames.campaigns,
    )
    const campaignManagerItem = navItems.find(
      ({ name }) => name === ENavItemNames.campaignManager,
    )

    if (overviewItem) {
      customAccessNavItems.push(overviewItem)
    }

    if (canAccessAiAssistantConfig || canAccessKnowledgeBase) {
      const conversationSettingsNav = navItems.find(
        ({ name }) => name === ENavItemNames.conversationSettings,
      )
      const knowledgeNavChild = conversationSettingsNav?.children?.find(
        (child) => child.path === EClientPath.settingsKnowledge,
      )
      const liveTransfersNavChild =
        import.meta.env[
          EEnvironmentVariables.VITE_IS_LIVE_TRANSFERS_ENABLED
        ] === 'true'
          ? conversationSettingsNav?.children?.find(
              (child) => child.path === EClientPath.liveTransfersScenarios,
            )
          : undefined

      const scenariosNavChild =
        import.meta.env[EEnvironmentVariables.VITE_IS_SCENARIOS_ENABLED] ===
        'true'
          ? conversationSettingsNav?.children?.find(
              (child) => child.path === EClientPath.manageScenariosV2,
            )
          : undefined

      customAccessNavItems.push(
        {
          name: ENavSectionNames.assistantSetup,
          isSectionHeader: true,
        },
        {
          name: ENavItemNames.conversationSettings,
          icon: <Settings sx={{ width: rem(17), height: rem(17) }} />,
          children: [
            ...(canAccessAiAssistantConfig
              ? [
                  {
                    name: ENavItemNames.settingsCoreBehavior,
                    path: EClientPath.settingsCoreBehavior,
                  },
                ]
              : []),
            ...(canAccessKnowledgeBase && knowledgeNavChild
              ? [knowledgeNavChild]
              : []),
            ...(canAccessAiAssistantConfig && liveTransfersNavChild
              ? [liveTransfersNavChild]
              : []),
            ...(canAccessAiAssistantConfig && scenariosNavChild
              ? [scenariosNavChild]
              : []),
          ],
        },
        ...(canAccessAiAssistantConfig
          ? [
              {
                name: ENavItemNames.channels,
                icon: <Devices sx={{ width: rem(17), height: rem(17) }} />,
                children: [
                  {
                    name: ESettingsStepFriendlyNames.chatbot,
                    path: EClientPath.settingsChatbot,
                  },
                  {
                    name: ESettingsStepFriendlyNames.email,
                    path: EClientPath.settingsEmail,
                  },
                  {
                    name: ESettingsStepFriendlyNames.text,
                    path: EClientPath.settingsText,
                  },
                  {
                    name: ESettingsStepFriendlyNames.phone,
                    path: EClientPath.settingsPhone,
                  },
                ],
              },
            ]
          : []),
      )
    }

    if (canReadCampaigns) {
      if (campaignsHeader) {
        customAccessNavItems.push(campaignsHeader)
      }
      if (campaignManagerItem) {
        customAccessNavItems.push(campaignManagerItem)
      }
    }

    const voipSectionHeader = navItems.find(
      ({ name }) => name === ENavSectionNames.voip,
    )
    const voipPhoneNumbersItem = navItems.find(
      ({ name }) => name === ENavItemNames.voipPhoneNumbers,
    )
    const voipDevicesItem = navItems.find(
      ({ name }) => name === ENavItemNames.voipDevices,
    )
    const voipCallsItem = navItems.find(
      ({ name }) => name === ENavItemNames.voipCalls,
    )

    const needsVoipSection =
      hasVoipOrgFeature && (canAccessVoipConfig || canAccessVoipVoicemails)
    if (needsVoipSection && voipSectionHeader) {
      customAccessNavItems.push(voipSectionHeader)
    }
    if (hasVoipOrgFeature && canAccessVoipConfig) {
      if (voipPhoneNumbersItem) {
        customAccessNavItems.push(voipPhoneNumbersItem)
      }
      if (voipDevicesItem) {
        customAccessNavItems.push(voipDevicesItem)
      }
    }
    if (hasVoipOrgFeature && canAccessVoipVoicemails && voipCallsItem) {
      customAccessNavItems.push(voipCallsItem)
    }

    const notificationsItem = navItems.find(
      ({ name }) => name === ENavItemNames.notifications,
    )
    const postConvoAnalysisItem = navItems.find(
      ({ name }) => name === ENavItemNames.postConvoSettings,
    )

    const shouldShowConversationsSection =
      (canAccessAiAssistantConfig &&
        (!!notificationsItem || !!postConvoAnalysisItem)) ||
      (canReadCrm && (!!inboxItem || !!contactsItem))

    if (shouldShowConversationsSection) {
      if (conversationsHeader) {
        customAccessNavItems.push(conversationsHeader)
      }
      if (canAccessAiAssistantConfig && notificationsItem) {
        customAccessNavItems.push(notificationsItem)
      }
      if (canAccessAiAssistantConfig && postConvoAnalysisItem) {
        customAccessNavItems.push(postConvoAnalysisItem)
      }
      if (canReadCrm) {
        if (inboxItem) {
          customAccessNavItems.push(inboxItem)
        }
        if (contactsItem) {
          customAccessNavItems.push(contactsItem)
        }
      }
    }

    if (canReadReporting && reportsItem) {
      customAccessNavItems.push(reportsItem)
    }

    return [
      ...customAccessNavItems,
      ...(isPartner
        ? [partnerHelpNavLinks]
        : [replifyHelpNavLinks, replifyTermsAndPoliciesNavLinks]),
    ]
  }

  return isPartner
    ? filterNavItems(
        navItems.concat([partnerHelpNavLinks]),
        navItemNamesHiddenForPartners,
      )
    : filterNavItems(
        navItems.concat([replifyHelpNavLinks, replifyTermsAndPoliciesNavLinks]),
        hiddenNavItemsForHeyLibby,
      )
}

export default useNavItems
